<template>
  <section class="slot-inspector-department">
    <div class="slot-inspector-department__heading">
      <template v-if="section.name">
        <h3 class="slot-inspector-department__name">{{ section.name }}</h3>
        <p v-if="section.address" class="slot-inspector-department__address">{{ section.address }}</p>
      </template>
      <p class="slot-inspector-department__ids">{{ idsLine }}</p>
    </div>

    <sp-table
      v-preserve-properties:columns="columns"
      v-preserve-properties:items="rows"
      v-preserve-properties:expansion="expansion"
      class="slot-inspector-department__table"
      row-key="key"
      striped
      hover
    ></sp-table>
  </section>
</template>

<script setup>
import { computed, h } from "vue";
import { vPreserveProperties } from "#ce/utils/property-bindings";
import { t } from "#ce/apps/SpAdmin/SpSlotInspectorApp/i18n";
import { createVisitTypeColumns, visitTypeRows } from "#ce/apps/SpAdmin/SpSlotInspectorApp/visit-type-columns";
import SlotInspectorSlotTable from "./SlotInspectorSlotTable.ce.vue";

const props = defineProps({
  /**
   * One payload section: a provider's department and its visit-type groups.
   */
  section: {
    type: Object,
    required: true,
  },
  /**
   * Copy for each reason a slot can carry, keyed by reason.
   */
  reasons: {
    type: Object,
    default: () => ({}),
  },
  /**
   * Show the provider's id next to the department's.
   */
  showProviderIds: {
    type: Boolean,
    default: false,
  },
});

const columns = createVisitTypeColumns();
const rows = computed(() => visitTypeRows(props.section.visitTypes));

const idsLine = computed(() => {
  const ids = [];

  if (props.showProviderIds && props.section.providerId) {
    ids.push(t("provider_id", { id: props.section.providerId }));
  }

  if (props.section.departmentId) {
    ids.push(t("department_id", { id: props.section.departmentId }));
  }

  return ids.join(" · ");
});

function expansion(row) {
  return h(SlotInspectorSlotTable, { slots: row.slots, reasons: props.reasons });
}
</script>

<style scoped lang="scss">
.slot-inspector-department {
  display: grid;
  gap: var(--sp-ref-scale-16);

  &__heading {
    display: grid;
    gap: var(--sp-ref-scale-2);
  }

  &__name,
  &__address,
  &__ids {
    margin: 0;
    font-size: var(--sp-sys-typescale-small-font-size);
    line-height: var(--sp-ref-scale-20);
    color: var(--sp-sys-color-on-surface);
  }

  &__name {
    font-weight: var(--sp-sys-font-weight-medium);
  }

  &__address {
    font-weight: var(--sp-sys-font-weight-light);
  }

  &__ids {
    color: var(--sp-sys-color-neutral-secondary);
  }

  &__table {
    --sp-comp-table-border-radius: var(--sp-ref-border-radius-sm);
    --sp-comp-table-cell-padding-block: var(--sp-ref-scale-10);
    --sp-comp-table-cell-padding-inline: var(--sp-ref-scale-16);
    --sp-comp-table-header-font-weight: var(--sp-sys-font-weight-medium);

    // sp-table leaves its cells' text to inheritance, so these reach the slot tables in the expanded rows too, which
    // set a lighter weight of their own.
    color: var(--sp-sys-color-on-surface);
    font-weight: var(--sp-sys-font-weight-normal);
    line-height: var(--sp-ref-scale-20);
  }
}
</style>
